import { logger } from '@/engine/logging';
import { getAllItems } from '@/generated/items';

export interface ItemInfo {
  id: string;
  name: string;
  icon: string;
  type: string;
  tier: number;
  basePrice: number;
  sellPrice: number;
  floors: string | null;
}

export class ItemsDB {
  private static instance: ItemsDB | null = null;
  private itemsCache = new Map<string, ItemInfo>();

  public static getInstance(): ItemsDB {
    if (!ItemsDB.instance) {
      ItemsDB.instance = new ItemsDB();
    }
    return ItemsDB.instance;
  }

  constructor() {
    this.initializeItems();
  }

  /**
   * Инициализация кэша предметов
   */
  private initializeItems(): void {
    const allItems = getAllItems();

    for (const item of allItems) {
      const itemInfo: ItemInfo = {
        id: item.item_id,
        name: item.name,
        icon: item.icon || 'icons/credit.png',
        type: item.type,
        tier: item.tier,
        basePrice: item.base_price,
        sellPrice: item.sell_price,
        floors: item.floors,
      };

      this.itemsCache.set(item.item_id, itemInfo);
    }

    logger.info('📦 ItemsDB: предметы инициализированы', {
      count: this.itemsCache.size,
      source: 'items',
    });
  }

  /**
   * Получить информацию о предмете по ID
   */
  getItem(itemId: string): ItemInfo | null {
    return this.itemsCache.get(itemId) || null;
  }

  /**
   * Получить название предмета
   */
  getItemName(itemId: string): string {
    const item = this.getItem(itemId);
    return item?.name || itemId;
  }

  /**
   * Получить иконку предмета
   */
  getItemIcon(itemId: string): string {
    const item = this.getItem(itemId);
    return item?.icon || 'icons/credit.png';
  }

  /**
   * Получить цену продажи предмета
   */
  getItemSellPrice(itemId: string): number {
    const item = this.getItem(itemId);
    return item?.sellPrice || 0;
  }

  /**
   * Получить базовую цену предмета
   */
  getItemBasePrice(itemId: string): number {
    const item = this.getItem(itemId);
    return item?.basePrice || 0;
  }

  /**
   * Получить тип предмета
   */
  getItemType(itemId: string): string {
    const item = this.getItem(itemId);
    return item?.type || 'unknown';
  }

  /**
   * Получить тир предмета
   */
  getItemTier(itemId: string): number {
    const item = this.getItem(itemId);
    return item?.tier || 0;
  }

  /**
   * Получить все предметы определенного типа
   */
  getItemsByType(type: string): ItemInfo[] {
    return Array.from(this.itemsCache.values()).filter(item => item.type === type);
  }

  /**
   * Получить все руды
   */
  getOres(): ItemInfo[] {
    return this.getItemsByType('ore');
  }

  /**
   * Получить все слитки
   */
  getBars(): ItemInfo[] {
    return this.getItemsByType('bar');
  }

  /**
   * Получить все валюты
   */
  getCurrencies(): ItemInfo[] {
    return this.getItemsByType('currency');
  }

  /**
   * Получить все предметы
   */
  getAllItems(): ItemInfo[] {
    return Array.from(this.itemsCache.values());
  }

  /**
   * Проверить существование предмета
   */
  hasItem(itemId: string): boolean {
    return this.itemsCache.has(itemId);
  }

  /**
   * Получить предметы по тиру (от и до)
   */
  getItemsByTier(minTier: number, maxTier: number = minTier): ItemInfo[] {
    return Array.from(this.itemsCache.values()).filter(
      item => item.tier >= minTier && item.tier <= maxTier
    );
  }

  /**
   * Получить предметы по миру
   */
  getItemsByWorld(_world: number): ItemInfo[] {
    // Пока у нас только мир 1, но можно расширить
    return Array.from(this.itemsCache.values());
  }

  /**
   * Поиск предметов по названию
   */
  searchItems(query: string): ItemInfo[] {
    const lowerQuery = query.toLowerCase();
    return Array.from(this.itemsCache.values()).filter(
      item =>
        item.name.toLowerCase().includes(lowerQuery) || item.id.toLowerCase().includes(lowerQuery)
    );
  }

  /**
   * Получить статистику предметов
   */
  getItemsStats() {
    const allItems = this.getAllItems();
    const typeStats = new Map<string, number>();
    const tierStats = new Map<number, number>();

    for (const item of allItems) {
      typeStats.set(item.type, (typeStats.get(item.type) || 0) + 1);
      tierStats.set(item.tier, (tierStats.get(item.tier) || 0) + 1);
    }

    return {
      totalItems: allItems.length,
      typeStats: Object.fromEntries(typeStats),
      tierStats: Object.fromEntries(tierStats),
      averagePrice: allItems.reduce((sum, item) => sum + item.sellPrice, 0) / allItems.length,
    };
  }
}

export const itemsDB = ItemsDB.getInstance();
